import { useMemo, useState, useCallback } from "react";
import type { ArtistIntake } from "../../domain/schemas";
import { ReadinessDial } from "../components/ReadinessDial";
import { loadJson } from "../utils";
import { computePersistentBlockers } from "../../domain/product";
import type { EvidenceQualityReport } from "../../domain/darkRoom";
import { FREE_PREPARATION_INPUT_CHARACTERS } from "../../domain/aiRouter";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SONG_TEXT_MIN = 20;

const GOAL_PRESETS = [
  "release strategy",
  "sync licensing",
  "brand clarity",
  "distribution deal",
  "splits review",
  "audience growth",
  "live performance",
  "label pitch"
] as const;

function isValidUrl(v: string): boolean {
  try { const u = new URL(v); return u.protocol === "https:" || u.protocol === "http:"; } catch { return false; }
}

function getUrlDomain(v: string): string {
  try { return new URL(v).hostname; } catch { return ""; }
}

function TagPillInput({
  values,
  onChange,
  placeholder,
  showLinkMarker
}: {
  values: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  showLinkMarker?: boolean;
}) {
  const [inputValue, setInputValue] = useState("");

  function commit() {
    const trimmed = inputValue.trim();
    if (trimmed && !values.includes(trimmed)) {
      onChange([...values, trimmed]);
    }
    setInputValue("");
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      commit();
    }
    if (e.key === "Backspace" && !inputValue && values.length > 0) {
      onChange(values.slice(0, -1));
    }
  }

  function remove(value: string) {
    onChange(values.filter((v) => v !== value));
  }

  return (
    <div className="tagPillContainer" onClick={() => document.querySelector<HTMLInputElement>(".tagPillInput")?.focus()}>
      {values.map((v) => {
        const isUrl = showLinkMarker && isValidUrl(v);
        const domain = isUrl ? getUrlDomain(v) : "";
        return (
          <span key={v} className="tagPill" title={isUrl ? `${domain} — supplied link, not opened` : undefined}>
            {isUrl && (
              <span className="tagPillFavicon" aria-hidden="true">↗</span>
            )}
            {v}
            <button type="button" onClick={() => remove(v)} aria-label={`Remove ${v}`}>×</button>
          </span>
        );
      })}
      <input
        className="tagPillInput"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={commit}
        placeholder={values.length === 0 ? placeholder : ""}
      />
    </div>
  );
}

export function IntakeStep({
  intake,
  update,
  onClear,
  onNext,
  liveReadinessScore,
  evidenceQuality
}: {
  intake: ArtistIntake;
  update: <K extends keyof ArtistIntake>(key: K, value: ArtistIntake[K]) => void;
  onClear: () => void;
  onNext: () => void;
  liveReadinessScore?: number;
  evidenceQuality?: EvidenceQualityReport | null;
}) {
  const [nameError, setNameError] = useState(false);
  const [emailBlurred, setEmailBlurred] = useState(false);
  const [showGapPanel, setShowGapPanel] = useState(false);
  const [showDraftNotice, setShowDraftNotice] = useState(true);
  const [showPrevRelease, setShowPrevRelease] = useState(!!intake.previousRelease);

  const updatePrevRelease = useCallback((field: "title" | "releasedAt" | "platform", val: string) => {
    const base = intake.previousRelease ?? { title: "", releasedAt: "", platform: "" };
    const updated = { ...base, [field]: val };
    const isEmpty = !updated.title && !updated.releasedAt && !updated.platform;
    update("previousRelease", isEmpty ? undefined : updated);
  }, [intake.previousRelease, update]);
  const maxChars = FREE_PREPARATION_INPUT_CHARACTERS;

  const lastSnapshot = useMemo<{ intake: ArtistIntake; openGates: string[]; readinessScore: number } | null>(() =>
    loadJson("shadow-last-review-snapshot", null), []);

  const persistentBlockers = useMemo(() => {
    const history = loadJson<Array<{ artistName?: string; openGates?: string[] }>>("shadow-signal-history", []);
    const artistHistory = history.filter((e) => e.artistName === intake.artistName.trim());
    return computePersistentBlockers(artistHistory);
  }, [intake.artistName]);

  function applyLastSession() {
    if (!lastSnapshot) return;
    const keys = Object.keys(lastSnapshot.intake) as (keyof ArtistIntake)[];
    for (const k of keys) update(k, lastSnapshot.intake[k] as ArtistIntake[typeof k]);
    setShowGapPanel(true);
  }
  const songTextLen = (intake.songText ?? "").length;
  const hasContent = Boolean(intake.artistName || intake.email || intake.genre || intake.songText || intake.links.length || intake.goals.length);

  const nameValid = intake.artistName.trim().length > 0;
  const emailFilled = (intake.email ?? "").trim().length > 0;
  const emailValid = !emailFilled || EMAIL_RE.test(intake.email ?? "");
  const emailError = emailBlurred && emailFilled && !emailValid;
  const songTextShort = songTextLen > 0 && songTextLen < SONG_TEXT_MIN;

  function handleNext() {
    if (!nameValid) {
      setNameError(true);
      return;
    }
    setNameError(false);
    onNext();
  }

  return (
    <section className="workspace">
      <form className="panel intake" onSubmit={(event) => event.preventDefault()}>
        <p className="panelKicker">01 / Artist evidence</p>
        <h2>Tell SHADOW enough to be useful.</h2>
        {lastSnapshot && !hasContent && (
          <button type="button" className="improvementModeBtn" onClick={applyLastSession}>
            Improve from last session (readiness was {lastSnapshot.readinessScore})
          </button>
        )}
        {showGapPanel && lastSnapshot && lastSnapshot.openGates.length > 0 && (
          <div className="gapTargetingPanel">
            <p>Gaps from your last session — address these to improve your score:</p>
            <ul>{lastSnapshot.openGates.slice(0, 5).map((g) => <li key={g}>{g.replaceAll("_", " ")}</li>)}</ul>
            {persistentBlockers.length > 0 && (
              <div className="persistentBlockerChips" role="list" aria-label="Persistent open gates">
                {persistentBlockers.slice(0, 4).map(({ gate, sessions }) => (
                  <span key={gate} className="persistentBlockerChip" role="listitem" title={`Open in ${sessions} sessions`}>
                    Persistent: {gate.replaceAll("_", " ")} ({sessions}×)
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
        {hasContent && intake.artistName.trim() && showDraftNotice && (
          <div className="draftNotice" role="status">
            <span>Continuing draft for <strong>{intake.artistName}</strong></span>
            <div className="draftNoticeActions">
              <button
                type="button"
                className="draftNoticeStartFresh"
                onClick={onClear}
              >
                Clear intake form
              </button>
              <button
                type="button"
                className="draftNoticeDismiss"
                onClick={() => setShowDraftNotice(false)}
                aria-label="Dismiss draft notice"
              >
                ×
              </button>
            </div>
          </div>
        )}
        {hasContent && !intake.artistName.trim() && (
          <p style={{ fontSize: "0.82rem", color: "var(--text-soft)", margin: "0 0 18px" }}>
            Continuing from a previous session.{" "}
            <button
              type="button"
              style={{ background: "none", border: 0, padding: 0, color: "var(--accent-alt)", cursor: "pointer", fontFamily: "inherit", fontSize: "0.82rem", textDecoration: "underline" }}
              onClick={onClear}
            >
              Clear intake form
            </button>
          </p>
        )}
        <label>
          <span className="fieldLabelRow">
            Artist name
            {nameValid && <span className="fieldValid">✓</span>}
          </span>
          <input
            value={intake.artistName}
            onChange={(event) => { setNameError(false); update("artistName", event.target.value); }}
            placeholder="Your artist name"
            className={nameError ? "fieldError" : nameValid ? "fieldOk" : ""}
          />
          {nameError && <small className="statusError">Artist name is required to continue.</small>}
        </label>
        <label>
          <span className="fieldLabelRow">
            Email <small style={{ color: "var(--text-soft)", fontWeight: 400 }}>(optional)</small>
            {emailFilled && emailValid && <span className="fieldValid">✓</span>}
          </span>
          <input
            type="email"
            value={intake.email}
            onChange={(event) => update("email", event.target.value)}
            onBlur={() => setEmailBlurred(true)}
            placeholder="your@email.com"
            className={emailError ? "fieldError" : emailFilled && emailValid ? "fieldOk" : ""}
          />
          {emailError && <small className="statusError">Email format looks off — check the address.</small>}
        </label>
        <label>
          Genre / lane
          <input value={intake.genre ?? ""} onChange={(event) => update("genre", event.target.value)} placeholder="e.g. dark cinematic hip-hop" />
        </label>
        <label>
          Goals <small style={{ color: "var(--text-soft)", fontWeight: 400 }}>(Enter or comma to add)</small>
          <div className="goalPresets">
            {GOAL_PRESETS.filter((p) => !intake.goals.includes(p)).map((preset) => (
              <button
                key={preset}
                type="button"
                className="goalPresetPill"
                onClick={() => update("goals", [...intake.goals, preset])}
              >
                {preset}
              </button>
            ))}
          </div>
          <TagPillInput
            values={intake.goals}
            onChange={(values) => update("goals", values)}
            placeholder="e.g. release strategy"
          />
        </label>
        <label>
          Public links <small style={{ color: "var(--text-soft)", fontWeight: 400 }}>(Enter or comma to add)</small>
          <TagPillInput
            values={intake.links}
            onChange={(values) => update("links", values)}
            placeholder="e.g. https://soundcloud.com/…"
            showLinkMarker
          />
        </label>
        <label>
          <span style={{ display: "flex", justifyContent: "space-between" }}>
            <span className="fieldLabelRow">
              Lyrics / evidence
              {songTextLen >= SONG_TEXT_MIN && <span className="fieldValid">✓</span>}
            </span>
            <span className="charCount">{songTextLen}/{maxChars}</span>
          </span>
          <textarea
            value={intake.songText ?? ""}
            onChange={(event) => update("songText", event.target.value)}
            maxLength={maxChars}
            aria-invalid={songTextLen > maxChars || undefined}
            placeholder="Paste a lyric, hook, or verse. Even a working title helps."
          />
          {songTextLen > maxChars && <small className="statusError" role="alert">This saved draft exceeds the {maxChars.toLocaleString()}-character free preparation limit. Shorten it before evaluation; your text has been preserved.</small>}
          {songTextLen > 0 && songTextLen <= maxChars && (() => {
            const band = songTextLen < 168 ? "thin" : songTextLen < 336 ? "medium" : "dense";
            const bandMap = {
              thin: { label: "Thin evidence", hint: "Add more lyrics, a hook, or notes — more context, sharper analysis.", color: "var(--status-warn, #f0a500)" },
              medium: { label: "Medium evidence", hint: "Good start. Add another verse or more context for a fuller Signal.", color: "var(--accent-alt, #7c5cbf)" },
              dense: { label: "Dense evidence — full Dark Room signal unlocked", hint: "You've given SHADOW enough to work with.", color: "var(--status-ok, #2ecc71)" }
            };
            const { label, hint, color } = bandMap[band];
            return (
              <div className="evidenceQualityBar" role="status" aria-live="polite" style={{ marginTop: "6px", fontSize: "0.82rem", color }}>
                <span style={{ fontWeight: 600 }}>{label}</span>
                <span style={{ color: "var(--text-soft)", marginLeft: "6px" }}>{hint}</span>
              </div>
            );
          })()}
        </label>
        {liveReadinessScore !== undefined && (
          <div className="liveReadinessPreview" aria-live="polite">
            <span className="liveReadinessLabel">Live preview — updates as you add evidence</span>
            <ReadinessDial readinessScore={liveReadinessScore} status="preview" />
          </div>
        )}
        {evidenceQuality && (
          <div className={`evidenceQualityBar tier-${evidenceQuality.tier}`} aria-label={`Evidence quality: ${evidenceQuality.tier}`}>
            <div className="eqHeader">
              <span className="eqTierLabel">Evidence quality</span>
              <span className="eqTierBadge">{evidenceQuality.tier}</span>
              <div className="eqScoreBar">
                <div className="eqScoreFill" style={{ width: `${evidenceQuality.score}%` }} />
              </div>
            </div>
            {evidenceQuality.gaps.length > 0 && (
              <ul className="eqGapList">
                {evidenceQuality.gaps.slice(0, 2).map((g) => <li key={g}>{g}</li>)}
              </ul>
            )}
          </div>
        )}
        <div className="critiqueModeRow">
          <span className="critiqueModeLabel">Critique style</span>
          <div className="critiqueModeChips">
            {(["direct", "constructive", "mentor"] as const).map((mode) => (
              <button
                key={mode}
                type="button"
                className={`critiqueModeChip ${(intake.critiqueMode ?? "direct") === mode ? "active" : ""}`}
                onClick={() => update("critiqueMode", mode)}
              >
                {mode === "direct" ? "Direct" : mode === "constructive" ? "Constructive" : "Mentor"}
              </button>
            ))}
          </div>
        </div>
        <div className="prevReleaseExpander">
          <button
            type="button"
            className="prevReleaseToggle"
            onClick={() => setShowPrevRelease((v) => !v)}
            aria-expanded={showPrevRelease}
          >
            {showPrevRelease ? "▾" : "▸"} Previous release <small style={{ color: "var(--text-soft)", fontWeight: 400 }}>(optional)</small>
          </button>
          {showPrevRelease && (
            <div className="prevReleaseFields">
              <label>
                Track or project title
                <input
                  value={intake.previousRelease?.title ?? ""}
                  onChange={(e) => updatePrevRelease("title", e.target.value)}
                  placeholder="e.g. Void Season EP"
                />
              </label>
              <label>
                Release date
                <input
                  value={intake.previousRelease?.releasedAt ?? ""}
                  onChange={(e) => updatePrevRelease("releasedAt", e.target.value)}
                  placeholder="e.g. March 2025"
                />
              </label>
              <label>
                Platform
                <input
                  value={intake.previousRelease?.platform ?? ""}
                  onChange={(e) => updatePrevRelease("platform", e.target.value)}
                  placeholder="e.g. Spotify, SoundCloud, Bandcamp"
                />
              </label>
            </div>
          )}
        </div>
        <div className="formActions">
          <button type="button" onClick={handleNext}>
            {!nameValid ? "Enter artist name to continue" : "Continue to consent"}
          </button>
        </div>
      </form>
      <article className="panel guidancePanel">
        <p className="panelKicker">Evidence discipline</p>
        <h2>Useful, not invasive.</h2>
        <p>Free Signal works from one song, public links, stated goals, and consent. Private catalog or voice data remains outside the MVP path.</p>
        <ul>
          <li>Input cap: {maxChars.toLocaleString()} characters for free preparation in every privacy setting.</li>
          <li>No audio upload or voice training in this flow.</li>
          <li>Artist-provided text is treated as evidence, not system instruction.</li>
        </ul>
      </article>
    </section>
  );
}
